import { snInstant, snLocalDate, snLocalDateTime } from "adl-gen/common";
import {
  type DbTable,
  snDbKey,
  texprDbColumnName,
  texprDbTable,
  type WithDbId,
} from "adl-gen/common/db";
import {
  type ATypeExpr,
  type DeclResolver,
  texprString,
} from "adl-gen/runtime/adl";
import {
  createJsonBinding,
  getAnnotation,
  type JsonBinding,
} from "adl-gen/runtime/json";
import * as adlast from "adl-gen/runtime/sys/adlast";
import { isEnum, scopedNamesEqual } from "adl-gen/runtime/utils";
import { snakeCase } from "change-case";

type Table<T> = Record<keyof T, T[keyof T]>;
type DbTableColumns<T extends Table<T>> = {
  [K in keyof T]: DbColumn<T, K>;
};
export type DbColumnValue = unknown;

/**
 *  Everything we need to know about a database table such that we
 *  can serialize/deserialize the columns in it.
 */
export interface DbTableDetails<T extends Table<T>> {
  tableName: string;
  fields: (keyof T)[];
  columns: DbTableColumns<T>;
}

export interface DbColumn<T extends Table<T>, K extends keyof T> {
  name: string;
  toDbValue(v: T[K]): DbColumnValue;
  fromDbValue(v: DbColumnValue): T[K];
}

export interface DbConversions<T> {
  toDbValue(v: T): DbColumnValue;
  fromDbValue(v: DbColumnValue): T;
}

/**
 * A table along with an alias used for querying
 */
export interface NamedTable<T extends Table<T>> {
  table: DbTableDetails<T>;
  alias: string;
}

export function namedTable<T>(
  table: DbTableDetails<T>,
  alias: string,
): NamedTable<T> {
  return { table, alias };
}

/**
 * Generate the details for an ADL defined database table
 */
export function getAdlTableDetails<T>(
  resolver: DeclResolver,
  typeExpr: ATypeExpr<T>,
): DbTableDetails<T> {
  const jbDbTable = createJsonBinding(resolver, texprDbTable());
  const jbDbColumnName = createJsonBinding(resolver, texprDbColumnName());

  if (typeExpr.value.typeRef.kind !== "reference") {
    throw new Error("Invalid ADL type for db access (not a type reference)");
  }
  const scopedDecl: adlast.ScopedDecl = resolver(typeExpr.value.typeRef.value);
  if (scopedDecl.decl.type_.kind !== "struct_") {
    throw new Error("Invalid ADL type for db access (not a struct)");
  }
  const dbTable: DbTable | undefined = getAnnotation(
    jbDbTable,
    scopedDecl.decl.annotations,
  );
  if (dbTable === undefined) {
    throw new Error("Invalid ADL type for db access (no DbTableAnnotation)");
  }

  const tableName =
    dbTable.tableName !== ""
      ? dbTable.tableName
      : snakeCase(scopedDecl.decl.name);
  const fields: (keyof T)[] = [];
  const columns: DbTableColumns<T> = {} as DbTableColumns<T>;

  scopedDecl.decl.type_.value.fields.forEach((field) => {
    const fieldName = field.name as keyof T;
    fields.push(fieldName);
    // Fix: createDbColumn looses type information
    columns[fieldName] = createDbColumn(resolver, jbDbColumnName, field);
  });

  return {
    tableName,
    fields,
    columns,
  };
}

/** Get the adl table details including the id field */
export function getAdlTableDetailsWithId<T>(
  resolver: DeclResolver,
  typeExpr: ATypeExpr<T>,
): DbTableDetails<T> {
  const jbDbColumnName = createJsonBinding(resolver, texprDbColumnName());
  const tableDetails = getAdlTableDetails(resolver, typeExpr);
  const id = "id" as keyof T;
  const idColumn = createDbColumn(
    resolver,
    jbDbColumnName,
    adlast.makeField({
      name: "id",
      serializedName: "id",
      default: { kind: "nothing" },
      typeExpr: texprString().value,
      annotations: [],
    }),
  );
  return {
    ...tableDetails,
    fields: [id, ...tableDetails.fields],
    columns: { [id]: idColumn, ...tableDetails.columns },
  };
}

/**
 * Extract an ADL defined value of Type T from a database row
 */
export function valueFromDbRow<T>(
  table: DbTableDetails<T>,
  row: Record<string, unknown>,
  columnPrefix?: string,
): WithDbId<T> {
  const id: string = columnPrefix
    ? (row[`${columnPrefix}_id`] as string)
    : (row.id as string);
  const value: T = {} as T;
  table.fields.forEach((fname) => {
    const column = table.columns[fname];
    const columnName = columnPrefix
      ? `${columnPrefix}_${column.name}`
      : `${column.name}`;
    value[fname] = column.fromDbValue(row[columnName]);
  });
  return {
    id,
    value,
  };
}

/**
 * Generates prefixed column names to be used in a query
 */
export function generateColumnNamesForTables<T>(
  namedTables: NamedTable<T>[],
): string[] {
  const colnames: string[] = [];
  for (const t of namedTables) {
    colnames.push(`${t.alias}.id ${t.alias}_id`);
    t.table.fields.forEach((fname) => {
      const column = t.table.columns[fname];
      colnames.push(`${t.alias}.${column.name} ${t.alias}_${column.name}`);
    });
  }
  return colnames;
}

/**
 * Extract an ADL defined value of Type T from a database row
 * given an aliased table
 */
export function valueFromDbRowAliasedTable<T>(
  table: NamedTable<T>,
  row: Record<string, unknown>,
): WithDbId<T> {
  return valueFromDbRow(table.table, row, table.alias);
}

/**
 * The SQL fragment for all of the columns from the given tables.
 */
export function sqlColumns<T>(ntables: NamedTable<T>[]): string {
  return String(generateColumnNamesForTables(ntables));
}

/**
 * The SQL fragment for a table with an alias
 */
export function sqlFromTable<T>(ntable: NamedTable<T>): string {
  return `${ntable.table.tableName} ${ntable.alias}`;
}

//-----------------------------------------------------------------------------
//Code to map typescript values into database columns

function createDbColumn(
  resolver: DeclResolver,
  jbDbColumnName: JsonBinding<string>,
  field: adlast.Field,
  //TODO: Fix generic types
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): DbColumn<any, any> {
  const ann = getAnnotation(jbDbColumnName, field.annotations);
  const name = ann === undefined ? snakeCase(field.name) : ann;
  const dbConversions = createDbConversions(resolver, field.typeExpr);
  return {
    name,
    toDbValue: dbConversions.toDbValue,
    fromDbValue: dbConversions.fromDbValue,
  };
}

function createDbConversions(
  resolver: DeclResolver,
  typeExpr: adlast.TypeExpr,
): DbConversions<unknown> {
  return createDbConversions0(resolver, typeExpr, true);
}

function createDbConversions0(
  resolver: DeclResolver,
  typeExpr: adlast.TypeExpr,
  allowNullable: boolean,
): DbConversions<unknown> {
  if (allowNullable) {
    if (
      typeExpr.typeRef.kind === "primitive" &&
      typeExpr.typeRef.value === "Nullable"
    ) {
      return nullableDbConversions(
        createDbConversions0(resolver, typeExpr.parameters[0], false),
      );
    }
    if (isMaybe(typeExpr)) {
      return nullableDbConversions(
        createDbConversions0(resolver, typeExpr.parameters[0], false),
      );
    }
  }
  if (
    typeExpr.typeRef.kind === "reference" &&
    scopedNamesEqual(typeExpr.typeRef.value, snInstant)
  ) {
    return INSTANT_DB_CONVERSIONS;
  }
  if (
    typeExpr.typeRef.kind === "reference" &&
    scopedNamesEqual(typeExpr.typeRef.value, snLocalDate)
  ) {
    return LOCALDATE_DB_CONVERSIONS;
  }
  if (
    typeExpr.typeRef.kind === "reference" &&
    scopedNamesEqual(typeExpr.typeRef.value, snLocalDateTime)
  ) {
    return LOCALDATETIME_DB_CONVERSIONS;
  }
  if (
    typeExpr.typeRef.kind === "reference" &&
    scopedNamesEqual(typeExpr.typeRef.value, snDbKey)
  ) {
    return IDENTITY_DB_CONVERSIONS;
  }
  const rTypeExpr = resolveTypeAliasOrNewtype(resolver, typeExpr);
  if (rTypeExpr !== undefined) {
    return createDbConversions0(resolver, rTypeExpr, allowNullable);
  }
  const union = resolveUnion(resolver, typeExpr);
  if (union && isEnum(union)) {
    return unionDbConversions(resolver, { value: typeExpr });
  }
  if (
    typeExpr.typeRef.kind === "primitive" &&
    typeExpr.parameters.length === 0
  ) {
    return IDENTITY_DB_CONVERSIONS;
  }
  return jsonDbConversions(resolver, { value: typeExpr });
}

function resolveTypeAliasOrNewtype(
  resolver: DeclResolver,
  typeExpr: adlast.TypeExpr,
): adlast.TypeExpr | undefined {
  if (
    typeExpr.typeRef.kind === "reference" &&
    typeExpr.parameters.length === 0
  ) {
    const scopedDecl = resolver(typeExpr.typeRef.value);
    if (scopedDecl.decl.type_.kind === "type_") {
      return scopedDecl.decl.type_.value.typeExpr;
    }
    if (scopedDecl.decl.type_.kind === "newtype_") {
      return scopedDecl.decl.type_.value.typeExpr;
    }
  }
  return undefined;
}

function resolveUnion(
  resolver: DeclResolver,
  typeExpr: adlast.TypeExpr,
): adlast.Union | undefined {
  if (typeExpr.typeRef.kind === "reference") {
    const scopedDecl = resolver(typeExpr.typeRef.value);
    if (scopedDecl.decl.type_.kind === "union_") {
      return scopedDecl.decl.type_.value;
    }
  }
  return undefined;
}

function isMaybe(typeExpr: adlast.TypeExpr): boolean {
  const snMaybe = { moduleName: "sys.types", name: "Maybe" };
  if (typeExpr.typeRef.kind === "reference") {
    return scopedNamesEqual(typeExpr.typeRef.value, snMaybe);
  }
  return false;
}

function nullableDbConversions<T>(
  dbConversions: DbConversions<T>,
): DbConversions<T | null> {
  function dbFromValue(v: T | null): DbColumnValue {
    return v === null ? null : dbConversions.toDbValue(v);
  }
  function valueFromDb(v: DbColumnValue): T | null {
    return v === null ? null : dbConversions.fromDbValue(v);
  }
  return { toDbValue: dbFromValue, fromDbValue: valueFromDb };
}

function jsonDbConversions<T>(
  resolver: DeclResolver,
  typeExpr: ATypeExpr<T>,
): DbConversions<T> {
  const jb = createJsonBinding(resolver, typeExpr);
  function dbFromValue(v: T) {
    return JSON.stringify(jb.toJson(v));
  }
  return {
    toDbValue: dbFromValue,
    fromDbValue: jb.fromJsonE,
  };
}

function unionDbConversions<T>(
  resolver: DeclResolver,
  typeExpr: ATypeExpr<T>,
): DbConversions<T> {
  const jb = createJsonBinding(resolver, typeExpr);
  return {
    toDbValue: jb.toJson,
    fromDbValue: jb.fromJsonE,
  };
}

const IDENTITY_DB_CONVERSIONS: DbConversions<unknown> = {
  toDbValue: (v) => v,
  fromDbValue: (v) => v,
};

export const INSTANT_DB_CONVERSIONS: DbConversions<number> = {
  toDbValue: (v: number) => new Date(v).toISOString(),
  fromDbValue: (v) => {
    if (typeof v === "number") {
      return v;
    }
    if (typeof v == "string") {
      return new Date(v).getTime();
    }
    return (v as Date).getTime();
  },
};

const LOCALDATE_DB_CONVERSIONS: DbConversions<string> = {
  toDbValue: (v) => v,
  fromDbValue: (v) => {
    if (typeof v !== "string" || !v.match(/\d\d\d\d-\d\d-\d\d/)) {
      throw new Error(
        `expected YYYY-MM-DD string from db date for localdate, got ${String(v)}`,
      );
    }
    return v;
  },
};

const LOCALDATETIME_DB_CONVERSIONS: DbConversions<string> = {
  toDbValue: (v) => v,
  fromDbValue: (v) => {
    if (
      typeof v !== "string" ||
      !v.match(/\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d(.\d+)?/)
    ) {
      throw new Error(
        `expected YYYY-MM-DDTHH:MM:SS.S string from db timestamp for localdatetime, got ${String(v)}`,
      );
    }
    return v;
  },
};
